// The scanner's home after sign-in: the dashboard's greeting, one scan field, then the
// tools this operator can open on this deployment (tools.tsx decides which). A scan of
// anything is routed here: a drone to its page, a box to its card, a rack section to its
// card, a board, part or asset to the sticker card. Every way back lands on the field, focused, ready for the next scan.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FOCUS, MOTION } from '../Requisitions/buttonStyles';
import { LOOKUP_MISS_TEXT, useStickerLookup } from '../utils/useStickerLookup';
import type { ScanLookupResult } from '../utils/scanDestination';
import type { ResolvedBox } from '../Parts/Pulls/pullsApi';
import { useScannerCaps } from './access';
import { logScannerDiagnostic } from './diagnostics';
import useScannerInput from './hooks/useScannerInput';
import { buzz } from './shell';
import { LockIcon, OpenIcon } from './icons';
import BoxCard from './Box/BoxCard';
import DronePage from './Drone/DronePage';
import RepairSession from './Drone/RepairSession';
import type { DroneSummary } from './Drone/types';
import { describeSticker } from './Lookup/describeSticker';
import StickerCard from './Lookup/StickerCard';
import SectionCard from './Section/SectionCard';
import type { SectionPayload } from './Section/types';
import type { StickerReading } from './Lookup/types';
import { focusForScan, Notice, ScanField, ScreenBar } from './ui';
import type { ScannerTool } from './types';

interface Props {
  firstname: string | null;
  tools: ScannerTool[];
  onSelect: (toolId: string) => void;
  onLock: () => void;
  /** Credited on a repair fit. The operator token already proves who it is on the kiosk. */
  operatorId?: string;
}

type View =
  | { name: 'home' }
  | { name: 'box'; box: ResolvedBox }
  | { name: 'section'; section: SectionPayload }
  | { name: 'drone'; serial: string }
  | { name: 'repair'; summary: DroneSummary }
  | { name: 'sticker'; reading: StickerReading; result: ScanLookupResult };

// Module scope so the hook's listeners keep a stable identity. The server decides what a
// code is; this only drops what cannot be a sticker at all.
const isStickerCode = (val: string) => /^\S{4,}$/.test(val);

const ToolRow: React.FC<{ tool: ScannerTool; only: boolean; onSelect: (id: string) => void }> = ({ tool, only, onSelect }) => (
  <li>
    <button
      type="button"
      onClick={() => onSelect(tool.id)}
      className={`flex min-h-[4.5rem] w-full items-center gap-4 rounded-xl bg-surface-container px-4 py-3 text-left np-state
        ${only ? 'ring-1 ring-inset ring-primary/40' : ''} ${FOCUS} ${MOTION}`}
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-container-highest text-on-surface-variant">
        <tool.Icon className="h-7 w-7" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-semibold text-on-surface">{tool.label}</span>
        <span className="block text-sm leading-snug text-on-surface-variant">{tool.description}</span>
      </span>
      <OpenIcon className="h-5 w-5 shrink-0 text-on-surface-variant" />
    </button>
  </li>
);

const ScannerHome: React.FC<Props> = ({ firstname, tools, onSelect, onLock, operatorId }) => {
  const caps = useScannerCaps();
  const { lookup } = useStickerLookup();
  const scanRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<View>({ name: 'home' });
  const [working, setWorking] = useState(false);
  // `n` re-keys the line so a second miss in a row lands again instead of sitting still.
  const [notice, setNotice] = useState<{ tone: 'ok' | 'miss'; text: string; n: number } | null>(null);

  const say = useCallback((tone: 'ok' | 'miss', text: string) => {
    if (tone === 'miss') buzz('reject');
    setNotice((prev) => ({ tone, text, n: (prev?.n ?? 0) + 1 }));
  }, []);

  const goHome = useCallback(() => {
    setNotice(null);
    setView({ name: 'home' });
  }, []);

  /** An action on a card finished: back to the field, with its sentence under it. */
  const finished = useCallback((sentence: string) => {
    setView({ name: 'home' });
    say('ok', sentence);
  }, [say]);

  // Home takes the next scan, whatever just happened.
  useEffect(() => {
    if (view.name === 'home') focusForScan(scanRef);
  }, [view.name, notice]);

  const route = useCallback(async (raw: string) => {
    setWorking(true);
    setNotice(null);
    const outcome = await lookup(raw);
    setWorking(false);
    if (outcome.status === 'ignored') return;
    if (outcome.status === 'miss') {
      setView({ name: 'home' });
      say(
        'miss',
        outcome.miss === 'not_found'
          ? `Nothing is registered under ${raw}. Check the sticker and scan it again.`
          : LOOKUP_MISS_TEXT.error,
      );
      if (outcome.miss === 'error') {
        logScannerDiagnostic({ surface: 'lookup', event: 'lookup_error', detail: { status: outcome.httpStatus } });
      }
      return;
    }

    const { result } = outcome;
    // The scanner lookup also answers `box`, which the shared ScanKind does not list.
    const kind = result.kind as string;
    const payload = result.payload;
    if (kind === 'drone' && payload?.serialnumber) {
      setView({ name: 'drone', serial: payload.serialnumber });
      return;
    }
    if (kind === 'box' && Array.isArray(payload?.items)) {
      setView({ name: 'box', box: payload as ResolvedBox });
      return;
    }
    if (kind === 'section' && payload?.code) {
      setView({ name: 'section', section: payload as SectionPayload });
      return;
    }
    const reading = describeSticker(result);
    if (reading) {
      setView({ name: 'sticker', reading, result });
      return;
    }
    setView({ name: 'home' });
    say('miss', `Nothing is registered under ${raw}. Check the sticker and scan it again.`);
  }, [lookup, say]);

  // Scans reach the home and the sticker card even when no field has focus. A box, a drone
  // or a repair has its own forms, which a stray scan must not clobber.
  useScannerInput({
    onScan: route,
    surface: 'lookup',
    minLength: 4,
    validator: isStickerCode,
    disabled: view.name !== 'home' && view.name !== 'sticker',
  });

  if (view.name === 'box') {
    return <BoxCard box={view.box} caps={caps} onBack={goHome} onFinished={finished} />;
  }

  if (view.name === 'section') {
    return <SectionCard section={view.section} onBack={goHome} />;
  }

  if (view.name === 'drone') {
    return (
      <DronePage
        serial={view.serial}
        caps={caps}
        onBack={goHome}
        onRepair={(summary) => setView({ name: 'repair', summary })}
      />
    );
  }

  if (view.name === 'repair') {
    return (
      <RepairSession
        summary={view.summary}
        operatorId={operatorId}
        onDone={() => setView({ name: 'drone', serial: view.summary.drone.serialnumber })}
      />
    );
  }

  if (view.name === 'sticker') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <ScreenBar title={view.reading.kind} onBack={goHome} />
        <div className="mx-auto w-full max-w-md px-4 pb-6">
          <StickerCard reading={view.reading} result={view.result} />
        </div>
      </div>
    );
  }

  return (
    <main className="flex w-full max-w-md flex-1 flex-col self-center px-4 pb-6 pt-8">
      {/* Firefleet's dashboard greeting, the same words and the same shining name. */}
      <h1 className="text-center font-mono text-xl text-on-surface">
        {firstname ? (
          <>Welcome, <span className="username motion-reduce:!animate-none">{firstname}</span>!</>
        ) : (
          'Welcome!'
        )}
      </h1>

      <div className="mt-6">
        <ScanField
          inputRef={scanRef}
          label="Scan any sticker or select an option below"
          busy={working}
          onSubmit={route}
        />
        <div aria-live="polite">
          {notice && (
            <div role={notice.tone === 'miss' ? 'alert' : undefined} className="mt-3">
              <Notice key={notice.n} tone={notice.tone}>{notice.text}</Notice>
            </div>
          )}
        </div>
      </div>

      {tools.length > 0 ? (
        // On a touch screen the list sits low, where the thumb of the hand holding the scanner reaches.
        <div className="mt-8 [@media(pointer:coarse)]:mt-auto">
          <ul className="flex flex-col gap-2" aria-label="Scanner tools">
            {tools.map((t) => (
              <ToolRow key={t.id} tool={t} only={tools.length === 1} onSelect={onSelect} />
            ))}
          </ul>
          <p className="mt-4 px-1 text-center text-xs text-on-surface-variant">
            Lock the scanner when you put it down. It locks itself after 10 minutes.
          </p>
        </div>
      ) : (
        <section className="mt-10 rounded-xl bg-surface-container-low p-5 text-center">
          <h2 className="text-base font-semibold text-on-surface">No scanner tools on your account</h2>
          <p className="mt-1 text-sm text-on-surface-variant">
            Ask a manager to give you the access you need, then sign in again.
          </p>
          <button
            type="button"
            onClick={onLock}
            className={`mt-4 inline-flex min-h-[2.75rem] items-center gap-2 rounded-full bg-surface-container-highest px-5 text-sm font-semibold text-on-surface np-state ${FOCUS} ${MOTION}`}
          >
            <LockIcon className="h-4 w-4" />
            Switch operator
          </button>
        </section>
      )}
    </main>
  );
};

export default ScannerHome;
